import {
  AccessoryCategory,
  IAccessoryCategory,
  IAccessoryCategoryData,
  Module
} from '@/models/configurator/module';
import { ModuleService } from '@/services/configurator/module';
import { ActionTree, GetterTree, MutationTree } from 'vuex';

export type ModuleState = typeof state;

const moduleService = new ModuleService();

export const state = {
  modules: [] as Module[],
  accessoryCategories: [] as IAccessoryCategoryData[]
};

export const mutations: MutationTree<ModuleState> = {
  ADD_MODULE(state, payload: Module) {
    state.modules.push(payload);
  },

  SET_MODULES(state, payload: Module[]) {
    state.modules = payload;
  },

  SET_MODULE_ACCESSORY_CATEGORIES(state, payload: IAccessoryCategoryData[]) {
    state.accessoryCategories = payload;
  },

  UPDATE_MODULE(state, payload: Module) {
    const index = state.modules.findIndex(module => module.id === payload.id);
    state.modules.splice(index, 1, payload);
  },

  REMOVE_MODULE(state, payload: number) {
    const index = state.modules.findIndex(module => module.id === payload);
    state.modules.splice(index, 1);
  }
};

export const actions: ActionTree<ModuleState, ModuleState> = {
  createModule: async ({ commit, state }, payload) =>
    await new Promise((resolve, reject) => {
      moduleService
        .createModule(payload)
        .then(module => {
          const index = state.modules.findIndex(s => s.id === module.id);
          if (index === -1) {
            commit('ADD_MODULE', module);
          } else {
            commit('UPDATE_MODULE', module);
          }
          resolve(module);
        })
        .catch(err => {
          reject(err);
        });
    }),

  fetchModules: ({ commit }) =>
    new Promise((resolve, reject) => {
      moduleService
        .fetchModules()
        .then(modules => {
          commit('SET_MODULES', modules);
          resolve(modules);
        })
        .catch(err => {
          reject(err);
        });
    }),

  fetchModule: ({ commit, state }, id: number) =>
    new Promise((resolve, reject) => {
      moduleService
        .fetchModule(id)
        .then(module => {
          const index = state.modules.findIndex(s => s.id === module.id);
          if (index === -1) {
            commit('ADD_MODULE', module);
          } else {
            commit('UPDATE_MODULE', module);
          }
          resolve(module);
        })
        .catch(err => {
          reject(err);
        });
    }),

  updateModule: ({ commit, state }, payload: Module) =>
    new Promise((resolve, reject) => {
      moduleService
        .updateModule(payload)
        .then(module => {
          const index = state.modules.findIndex(s => s.id === module.id);
          if (index === -1) {
            commit('ADD_MODULE', module);
          } else {
            commit('UPDATE_MODULE', module);
          }
          resolve(module);
        })
        .catch(err => {
          reject(err);
        });
    }),

  deleteModule: ({ commit }, id: number) =>
    new Promise<void>((resolve, reject) => {
      moduleService
        .deleteModule(id)
        .then(() => {
          commit('REMOVE_MODULE', id);
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    }),

  cloneModule: ({ commit }, target: number) =>
    new Promise<void>((resolve, reject) => {
      moduleService
        .cloneModule(target)
        .then(module => {
          commit('ADD_MODULE', module);
          resolve(module);
        })
        .catch(err => {
          reject(err);
        });
    }),

  updateModuleOrder: ({ commit, state }, payload: Module) =>
    new Promise((resolve, reject) => {
      moduleService
        .updateModuleOrder(payload)
        .then(module => {
          const index = state.modules.findIndex(s => s.id === module.id);
          if (index === -1) {
            commit('ADD_MODULE', module);
          } else {
            commit('UPDATE_MODULE', module);
          }
          resolve(module);
        })
        .catch(err => {
          reject(err);
        });
    }),

  fetchEmployeeAccessoryCategories(context, payload: number): Promise<void> {
    return moduleService
      .fetchModuleAccessoryCategories(payload)
      .then((categories: IAccessoryCategoryData[]) => {
        context.commit('SET_MODULE_ACCESSORY_CATEGORIES', categories);
      });
  }
};
export const getters: GetterTree<ModuleState, ModuleState> = {
  getModuleById: state => (id: number) => {
    return state.modules.find((module: any) => module.id === id);
  },

  getActiveModules: state =>
    state.modules.filter(module => !module.is_archived),

  getArchivedModules: state =>
    state.modules.filter(module => module.is_archived),

  getModuleAccessoryCategories: (state): IAccessoryCategory[] => {
    return state.accessoryCategories.map(
      category => new AccessoryCategory(category)
    );
  }
};
